/**
 * ModelHeaderPreview Component for UI-Model-1
 *
 * Displays a preview of the model header as the end user would see it.
 * Shows logo, title, and action buttons based on header configuration.
 * Clickable to select header section for configuration.
 *
 * Layout aligned with existing UI-Model HeaderSection.tsx
 */

import React from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { HideEyeIcon, ResetIcon, CloseIcon } from 'assests/GmsIcons';
import placeholder from '../../../../../../assests/img/placeholder_icon.svg';
import { mergeYourBuildWithDefaults, YOUR_BUILD_DEFAULT_CONFIG } from '../../config/widgetDefaults';

interface ModelHeaderPreviewProps {
  id?: string;
}

export const ModelHeaderPreview = ({ id }: ModelHeaderPreviewProps) => {
  const baseId = id || 'model-header-preview';
  const { state, dispatch } = useUIModelState();
  const headerConfig = state.model?.layout.header?.configuration;
  const isSelected = state.editorState.selectedSection === 'header';
  const isCenteredLayout = headerConfig?.titleLayout === 'Centre';

  // YourBuild mini widget config
  const rawYourBuildConfig = state.model?.layout?.sharedWidgets?.yourBuildWidget;
  const yourBuildConfig = mergeYourBuildWithDefaults({
    ...YOUR_BUILD_DEFAULT_CONFIG,
    ...(rawYourBuildConfig || {}),
  });
  const anyMiniWidgetFieldVisible =
    (yourBuildConfig.miniWidgetShowText ?? true) ||
    (yourBuildConfig.miniWidgetShowPrice ?? true) ||
    (yourBuildConfig.miniWidgetShowItemCount ?? true);
  const showMiniWidget = (headerConfig?.showYourBuild ?? false) && anyMiniWidgetFieldVisible;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({
      type: 'SET_SELECTED_SECTION',
      payload: { section: 'header' },
    });
  };

  // Mini widget element (reused in both layout branches)
  const MiniWidget = () => (
    <div role="button" data-testid="modelheaderpreview-modelheaderpreview-div-1"
      id={`${baseId}-your-build`}
      className="tw-flex tw-items-center tw-border tw-rounded tw-overflow-hidden tw-text-ink tw-bg-surface tw-text-xs tw-cursor-pointer hover:tw-bg-surface-hover tw-border-accent-bold"
      onClick={(e) => {
        e.stopPropagation();
        dispatch({ type: 'SET_SELECTED_SECTION', payload: { section: 'header-your-build' } });
      }}
    >
      <div className="tw-px-2 tw-py-1">
        {yourBuildConfig.miniWidgetShowText && (
          <div className="tw-font-semibold tw-leading-tight">
            {yourBuildConfig.miniWidgetText || 'Your Build'}
          </div>
        )}
        {yourBuildConfig.miniWidgetShowPrice && (
          <div className="tw-leading-tight">$0.00</div>
        )}
      </div>
      <div className="tw-flex tw-items-center">
        {yourBuildConfig.miniWidgetShowItemCount && (
          <div className="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-1 tw-font-semibold tw-bg-blue-100 tw-rounded">
            0 Item
          </div>
        )}
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );

  return (
    <div role="button"
      id={baseId}
      data-testid={baseId}
      onClick={handleClick}
      className={`tw-relative ${!headerConfig?.showHeader ? 'tw-min-h-[80px]' : 'tw-py-2 tw-px-6'} tw-mb-2 tw-flex tw-justify-between tw-items-center tw-h-auto tw-cursor-pointer tw-transition-all tw-border-2 tw-bg-surface ${
        isSelected
          ? 'tw-border-accent'
          : 'tw-border-transparent hover:tw-bg-surface-hover'
      }`}
    >
      {!headerConfig?.showHeader && (
        <div className="tw-absolute tw-inset-0 tw-bg-surface-sunken tw-flex tw-items-center tw-justify-center tw-z-10 tw-rounded-md">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-ink-secondary [&_path]:tw-stroke-current">
            <HideEyeIcon />
            <span
              className="tw-font-semibold tw-text-base"
              style={{ fontFamily: "'Noto Sans', sans-serif" }}
            >
              Header
            </span>
          </div>
        </div>
      )}
      {headerConfig?.showHeader && (isCenteredLayout ? (
        <>
          {/* Logo on left - fixed width */}
          <div
            id={`${baseId}-logo-wrapper`}
            className="tw-w-20 tw-flex tw-items-center tw-justify-center"
          >
            <img
              id={`${baseId}-logo`}
              src={headerConfig.logoUrl || placeholder}
              alt="Logo"
              className="tw-h-10 tw-w-auto tw-max-w-[80px]"
            />
          </div>

          {/* Title in center - takes remaining space */}
          {headerConfig.showTitle && headerConfig.titleText && (
            <h1
              id={`${baseId}-title`}
              className="tw-text-xl tw-text-ink tw-font-semibold tw-flex-1 tw-text-center tw-truncate tw-max-w-xs"
              title={headerConfig.titleText}
            >
              {headerConfig.titleText}
            </h1>
          )}

          {/* Buttons on right */}
          <div id={`${baseId}-buttons`} className="tw-flex tw-gap-2 tw-items-center">
            {showMiniWidget && <MiniWidget />}
            {headerConfig.showResetButton && (
              <button
                id={`${baseId}-reset-btn`}
                data-testid={`${baseId}-reset-btn`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
              >
                {headerConfig.resetButtonShowIcon && (
                  headerConfig.resetButtonIconUrl
                    ? <img id={`${baseId}-reset-btn-icon`} src={headerConfig.resetButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                    : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><ResetIcon /></span>
                )}
                {headerConfig.resetButtonText}
              </button>
            )}
            {headerConfig.showCloseButton && (
              <button
                id={`${baseId}-close-btn`}
                data-testid={`${baseId}-close-btn`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
              >
                {headerConfig.closeButtonShowIcon && (
                  headerConfig.closeButtonIconUrl
                    ? <img id={`${baseId}-close-btn-icon`} src={headerConfig.closeButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                    : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><CloseIcon /></span>
                )}
                {headerConfig.closeButtonText}
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Left section: Logo with title below */}
          <div id={`${baseId}-left-section`} className="tw-space-y-3">
            <div
              id={`${baseId}-logo-wrapper`}
              className="tw-w-20 tw-flex tw-items-center tw-justify-center"
            >
              <img
                id={`${baseId}-logo`}
                src={headerConfig.logoUrl || placeholder}
                alt="Logo"
                className="tw-h-10 tw-w-auto tw-max-w-[80px]"
                loading="lazy"
              />
            </div>
            {headerConfig.showTitle && headerConfig.titleText && (
              <h1
                id={`${baseId}-title`}
                className="tw-text-base tw-text-ink tw-font-semibold tw-truncate tw-max-w-xs"
                title={headerConfig.titleText}
              >
                {headerConfig.titleText}
              </h1>
            )}
          </div>

          {/* Buttons on right */}
          <div id={`${baseId}-buttons`} className="tw-flex tw-gap-2 tw-items-center">
            {showMiniWidget && <MiniWidget />}
            {headerConfig.showResetButton && (
              <button
                id={`${baseId}-reset-btn`}
                data-testid={`${baseId}-reset-btn`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
              >
                {headerConfig.resetButtonShowIcon && (
                  headerConfig.resetButtonIconUrl
                    ? <img id={`${baseId}-reset-btn-icon`} src={headerConfig.resetButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                    : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><ResetIcon /></span>
                )}
                {headerConfig.resetButtonText}
              </button>
            )}
            {headerConfig.showCloseButton && (
              <button
                id={`${baseId}-close-btn`}
                data-testid={`${baseId}-close-btn`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
              >
                {headerConfig.closeButtonShowIcon && (
                  headerConfig.closeButtonIconUrl
                    ? <img id={`${baseId}-close-btn-icon`} src={headerConfig.closeButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                    : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><CloseIcon /></span>
                )}
                {headerConfig.closeButtonText}
              </button>
            )}
          </div>
        </>
      ))}
    </div>
  );
};

export default ModelHeaderPreview;
